import React from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarX } from "lucide-react";

// A1 (F&F batch, 2026-02): non-blocking double-booking warning shown before
// an apply/RSVP when GET /api/me/conflicts reports a clash. severity
// "overlap" = two timed events genuinely intersect (strong copy);
// "same_day" = shares the calendar day (opportunities are date-only by
// design, so this is the honest signal — gentler copy).
const KIND_LABEL = { opportunity: "opportunity", help_request: "help request", gathering: "gathering" };

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const fmtWhen = (c) => c.starts_at
  ? new Date(c.starts_at).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
  : new Date(c.day).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });

export const ConflictWarningDialog = ({ open, onOpenChange, conflicts = [], severity, onConfirm, confirmLabel = "Sign up anyway" }) => {
  const first = conflicts[0];
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="conflict-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-forest flex items-center gap-2">
            <CalendarX className="w-5 h-5 text-terracotta" />
            {severity === "overlap" ? "This clashes with another commitment" : "You're already busy that day"}
          </AlertDialogTitle>
          <AlertDialogDescription data-testid="conflict-warning-copy">
            {first && (severity === "overlap"
              ? `This clashes with "${first.title}" at ${fmtTime(first.starts_at)} — sign up anyway?`
              : `You already have "${first.title}" on this day — double-check you can make both.`)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          {conflicts.slice(0, 3).map((c) => (
            <div key={c.id} data-testid={`conflict-item-${c.id}`}
              className="rounded-lg border border-neutral-200 bg-cream/40 px-3 py-2 text-sm text-neutral-700 flex items-baseline gap-2">
              <span className="font-medium text-forest truncate">{c.title}</span>
              <span className="text-xs text-neutral-500 shrink-0 ml-auto">
                {KIND_LABEL[c.kind] || "event"} · {fmtWhen(c)}
              </span>
            </div>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="conflict-go-back" className="rounded-full">Go back</AlertDialogCancel>
          <AlertDialogAction data-testid="conflict-proceed" onClick={onConfirm}
            className="rounded-full bg-forest hover:bg-forest-hover">
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
